import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
    children: React.ReactElement;
    allowedRoles?: string[];
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication and specific roles
 * 
 * Usage:
 * <ProtectedRoute allowedRoles={['project_manager', 'agronomist']}>
 *   <YourComponent />
 * </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
    const { user, isLoading, isAuthenticated } = useAuth();
    const location = useLocation();

    const routeAccessMap: Record<string, { page?: string; feature?: string }> = {
        '/dashboard': { page: 'dashboard', feature: 'dashboard' },
        '/dashboard/farmers': { page: 'farmers', feature: 'farmers' },
        '/dashboard/users': { page: 'users' },
        '/dashboard/website-control': { page: 'website-control', feature: 'website_control' },
        '/dashboard/blogs': { page: 'website-control', feature: 'website_control' },
        '/dashboard/cohorts': { page: 'cohorts', feature: 'cohorts' },
        '/dashboard/vsla': { page: 'vsla', feature: 'vsla' },
        '/dashboard/crops': { page: 'crops', feature: 'crops' },
        '/dashboard/traceability': { page: 'traceability', feature: 'traceability' },
        '/dashboard/activities': { page: 'activities', feature: 'activities' },
        '/dashboard/orders': { page: 'orders', feature: 'orders' },
        '/dashboard/compost': { page: 'compost', feature: 'compost' },
        '/dashboard/training': { page: 'training', feature: 'training' },
        '/dashboard/guidance': { page: 'training', feature: 'training' },
        '/dashboard/issues': { page: 'issues', feature: 'issues' },
        '/dashboard/warehouse': { page: 'warehouse', feature: 'warehouse' },
        '/dashboard/maps': { page: 'maps', feature: 'maps' },
        '/dashboard/money': { page: 'farm-finance', feature: 'farm_finance' },
        '/dashboard/analytics': { page: 'analytics' },
        '/dashboard/donations': { page: 'donations', feature: 'donations' },
        '/dashboard/funding-readiness': { page: 'funding-readiness', feature: 'funding_readiness' },
        '/dashboard/audit-logs': { page: 'audit-logs' },
    };

    const isEnabled = (kind: 'tenant_pages' | 'tenant_features', key?: string) => {
        if (!key || user?.role === 'super_admin') return true;
        const entry = user?.[kind]?.[key];
        if (!entry) return true;
        return entry.enabled !== false && entry.is_enabled !== false;
    };

    const routeAccess = routeAccessMap[location.pathname];
    const roleAliases: Record<string, string[]> = {
        tenant_admin: ['tenant_admin', 'project_manager'],
        business_manager: ['business_manager', 'project_manager'],
        field_manager: ['field_manager', 'project_manager', 'agronomist'],
        field_scout: ['field_scout', 'field_facilitator'],
        agronomist_advisor: ['agronomist_advisor', 'agronomist'],
    };

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated || !user) {
        return <Navigate to="/login" replace />;
    }

    // Role-based access: unauthorized roles are redirected to their dashboard
    // instead of showing a restriction page. Restricted pages are already hidden
    // from the navigation, so this only guards direct URL access.
    if (allowedRoles && allowedRoles.length > 0) {
        const effectiveRoles = roleAliases[user.role] || [user.role];
        if (!effectiveRoles.some((role) => allowedRoles.includes(role))) {
            return <Navigate to="/dashboard" replace />;
        }
    }

    // Disabled tenant modules: redirect to the dashboard instead of showing a
    // disabled-module page. Disabled entries are already hidden from navigation.
    if (routeAccess && (!isEnabled('tenant_pages', routeAccess.page) || !isEnabled('tenant_features', routeAccess.feature))) {
        return <Navigate to="/dashboard" replace />;
    }

    // User is authenticated and has required role
    return children;
};

export default ProtectedRoute;
